import React, { useContext, useState } from 'react';
import parse from 'html-react-parser';
import {
  ButtonToolbar,
  Container,
  Row,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { Formik, FormikProps } from 'formik';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLocation } from 'react-router';
import classnames from 'classnames';
import {
  PageHeader,
  Section,
  SpinnerArea,
  Button,
  Spinner,
  Modal,
  ConfirmModal,
} from '../../shared/components';
import adminService from '../../services/adminService';
import {
  RolePermissionFilter,
  RolePermissionUser,
  UserOperations,
} from '../../services/types/Admin';
import FormCheckboxGroup from '../../shared/components/form/FormCheckboxGroup';
import { OptionValue } from '../../services/types/Misc';
import useServiceReducer from '../../shared/hooks/useServiceReducer';
import reducer from './reducer';
import { KeyMatch } from '../../types';
import FormDropdown from '../../shared/components/form/FormDropdown';
import { dateTimeFormat } from '../../shared/misc/utils';
import AddMemberPopup from './AddMemberPopup';
import MainContext from '../App/MainProvider/context';
import RouteLeavingGuard from '../App/RouteLeavingGuard';
import Highlights from '../Operations/HighlightsAdmin';
import { HighlightCategory } from '../../services/types/Operation';
import FormCheckbox from '../../shared/components/form/FormCheckbox';
import Role from '../../types/role';

const ARROW_DOWN = 'arrow-down';
const ARROW_UP = 'arrow-up';
const roleEsg = 'ESG';
const roleGrievance = 'GP';

// Extraído a función standalone (Fase 7, deuda técnica T-2 - salto de Yup 0.x
// a 1.x) para poder testear el schema real de validación directo, sin montar
// el componente - ver src/containers/Admin/__tests__/rolesAndPermissionsSchema.test.ts.
// then/otherwise concatenados sobre el schema base (en vez de reemplazarlo) -
// misma semántica que Yup 0.x, que resolvía then/otherwise literales con
// schema.concat(branch) internamente.
export const buildRolesAndPermissionsSchema = (t: (key: string) => string) =>
  Yup.object().shape({
    users: Yup.array().of(
      Yup.object().shape({
        roleType: Yup.string().ensure(),
        role: Yup.string()
          .nullable()
          .when('roleType', {
            is: (val: string) => val === roleEsg,
            then: (schema: Yup.StringSchema<string | null | undefined>) =>
              schema.concat(Yup.string().required(t('required'))),
            otherwise: (schema: Yup.StringSchema<string | null | undefined>) =>
              schema.concat(Yup.string().notRequired()),
          }),
        grievanceRole: Yup.string()
          .nullable()
          .when('roleType', {
            is: (val: string) => val === roleGrievance,
            then: (schema: Yup.StringSchema<string | null | undefined>) =>
              schema.concat(Yup.string().required(t('required'))),
            otherwise: (schema: Yup.StringSchema<string | null | undefined>) =>
              schema.concat(Yup.string().notRequired()),
          }),
        group: Yup.string()
          .nullable()
          .when('roleType', ([roleType], schema: Yup.StringSchema<string | null | undefined>) => {
            if (roleType === roleEsg) {
              return schema.required(t('required')).when('role', {
                is: (val: string) => val === 'STF' || val === 'HSO',
                then: (s: Yup.StringSchema) => s.oneOf(['0'], 'Must be N/A'),
                otherwise: (s: Yup.StringSchema) => s.notOneOf(['0'], t('required')),
              });
            }
            return schema.notRequired().nullable();
          }),
      })
    ),
  });

const RolesAndPermissions: React.FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const { service, notify } = useContext(MainContext);

  const [data, loading, , , , reload] = useServiceReducer(
    adminService.getUserConfigurations,
    reducer,
    undefined
  );

  const [activeTab, setActiveTab] = useState('1');
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showErrorFlowsModal, setShowErrorFlowsModal] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [membersRemoved, setMembersRemoved] = useState<RolePermissionUser[]>([]);
  const [responseErrors, setResponseErrors] = useState<UserOperations[] | null>(null);
  const [formDirty, setFormDirty] = useState(false);
  const [dataForSubmit, setDataForSubmit] = useState<RolePermissionUser[] | null>(null);

  const toggleTab = (tab: string) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  const initFilter: RolePermissionFilter = {
    sortField: 'fullName' as KeyMatch<RolePermissionUser, string>,
    sortDescending: false,
  };
  const [filter, setFilter] = useState(initFilter);

  const removeMember = (
    formik: FormikProps<{ users: RolePermissionUser[] }>,
    email: string,
    roleType: string
  ) => {
    const forRemove = formik.values.users.find((x) => x.email === email && x.roleType === roleType);

    if (forRemove) {
      setMembersRemoved([...membersRemoved, forRemove]);
    }

    const newList = formik.values.users.filter(
      (x) => !(x.email === email && x.roleType === roleType)
    );

    formik.setValues({
      ...formik.values,
      users: newList,
    });
  };

  const addMember = (
    formik: FormikProps<{ users: RolePermissionUser[] }>,
    memberSelected: { email: string; fullName: string }
  ) => {
    if (memberSelected) {
      const members = [...formik.values.users];
      const currentRoleType = activeTab === '1' ? roleEsg : roleGrievance;

      if (members.some((x) => x.email === memberSelected.email && x.roleType === currentRoleType)) {
        return;
      }

      const memberInRemoved = membersRemoved.find(
        (x) => x.email === memberSelected.email && x.roleType === currentRoleType
      );

      const restoredRole = memberInRemoved ? memberInRemoved.role : null;
      const restoredGrievanceRole = memberInRemoved ? memberInRemoved.grievanceRole : 'GPT';
      const restoredGroup = memberInRemoved ? memberInRemoved.group : null;

      const user = {
        email: memberSelected.email,
        fullName: memberSelected.fullName,
        roleType: currentRoleType,
        role: currentRoleType === roleEsg ? restoredRole : 'STF',
        grievanceRole: currentRoleType === roleGrievance ? restoredGrievanceRole : null,
        group: currentRoleType === roleEsg ? restoredGroup : '0',
        permissions: memberInRemoved ? memberInRemoved.permissions : ['none'],
        modifiedOn: memberInRemoved ? memberInRemoved.modifiedOn : null,
        isSuperUser: memberInRemoved ? memberInRemoved.isSuperUser : false,
        isGuidanceService: memberInRemoved ? memberInRemoved.isGuidanceService : false,
        isGrievanceGH: false,
      } as RolePermissionUser;

      formik.setValues({
        ...formik.values,
        users: [user, ...formik.values.users],
      });

      setFormDirty(true);

      const newRemovedList = membersRemoved.filter(
        (x) => !(x.email === user.email && x.roleType === currentRoleType)
      );
      setMembersRemoved(newRemovedList);
    }

    setShowMembersModal(false);
  };

  const roleElements: OptionValue[] = [
    { id: 1, text: 'ESG Group Head', value: 'GH' },
    { id: 2, text: 'ESG Specialist', value: 'SPE' },
    { id: 3, text: 'ESG Member', value: 'STF' },
    { id: 4, text: 'ESG HSO', value: 'HSO' },
  ];

  const grievanceRoleElements: OptionValue[] = [
    { id: 1, text: 'GP Team Member', value: 'GPT' },
    { id: 2, text: 'GP Coordinator', value: 'GPC' },
  ];

  const groupElements: OptionValue[] = [
    { id: 1, text: '1', value: '1' },
    { id: 2, text: '2', value: '2' },
    { id: 3, text: '3', value: '3' },
    { id: 4, text: '4', value: '4' },
    { id: 5, text: '5', value: '5' },
    { id: 6, text: '6', value: '6' },
    { id: 7, text: 'N/A', value: '0' },
  ];

  const ghTypes = [
    { id: 1, text: 'E', value: 'GroupHeadEnv' },
    { id: 2, text: 'S', value: 'GroupHeadSoc' },
  ];

  const dccReviewer = [{ id: 1, text: '', value: 'DccReviewer' }];

  const changeSort = (
    formik: FormikProps<{ users: RolePermissionUser[] }>,
    column: keyof RolePermissionUser
  ) => {
    const descending = filter.sortDescending;
    const sortedList = [...formik.values.users].sort((a, b) => {
      if ((descending && a[column] === null) || (!descending && b[column] === null)) {
        return -1;
      }
      if ((descending && b[column] === null) || (!descending && a[column] === null)) {
        return 1;
      }

      // TS no puede acotar el índice genérico `a[column]`/`b[column]` a partir de los early-return
      // de arriba, pero ambos valores ya quedaron garantizados no-nulos en este punto en runtime.
      if (column === 'modifiedOn') {
        const timeA = new Date(a[column]!).getTime();
        const timeB = new Date(b[column]!).getTime();
        return descending ? timeB - timeA : timeA - timeB;
      }
      if (a[column]! < b[column]!) {
        return descending ? 1 : -1;
      }
      if (a[column]! > b[column]!) {
        return descending ? -1 : 1;
      }

      return 0;
    });

    setFilter((prev) => ({ ...filter, sortField: column, sortDescending: !prev.sortDescending }));
    formik.setValues({ ...formik.values, users: sortedList });
  };

  const discardChanges = () => setShowDiscardModal(true);

  const saveUserConfiguration = () => {
    if (dataForSubmit) {
      setIsSubmitting(true);
      service(() => adminService.saveConfiguration(dataForSubmit), {
        callback: (response) => {
          if (response.success) {
            notify(t('rolesAndPermissions'), 'Saved Successfully', 'success');
            reload();
            setFormDirty(false);
            setDataForSubmit(null);
            setMembersRemoved([]);
          } else if (response.assignedFlows && response.assignedFlows.length > 0) {
            setResponseErrors(response.assignedFlows);
            setShowErrorFlowsModal(true);
          } else if (response.assignedOperations && response.assignedOperations.length > 0) {
            setResponseErrors(response.assignedOperations);
            setShowErrorModal(true);
          } else if (response.errorList) {
            notify(
              t('rolesAndPermissions'),
              response.errorList[0].error,
              'danger',
              'exclamation-triangle'
            );
          } else {
            notify('Roles and Permissions', 'Error', 'danger', 'exclamation-triangle');
          }
        },
        loader: setIsSubmitting,
      });
    }
  };

  const renderErrorTable = () => {
    if (!responseErrors) {
      return null;
    }
    return responseErrors.map((x) => (
      <tr key={`responseError_tr_${x.user}`}>
        <td className="text-nowrap">{x.user}</td>
        <td>{parse(x.operations.reduce((acc, v) => `${acc}, <nobr>${v}</nobr>`, ''))}</td>
      </tr>
    ));
  };

  const getHighlights = (users: RolePermissionUser[]) => {
    const filterByGroup = (g: string) => users.filter((x) => x.group === g).length;
    return [
      {
        name: '',
        highlights: [
          { name: t('usersInSystem'), value: users.length, color: 'marine' },
          {
            name: t('usersInGroup', { groupNumber: '1' }),
            value: filterByGroup('1'),
            color: 'marine',
          },
          {
            name: t('usersInGroup', { groupNumber: '2' }),
            value: filterByGroup('2'),
            color: 'marine',
          },
          {
            name: t('usersInGroup', { groupNumber: '3' }),
            value: filterByGroup('3'),
            color: 'marine',
          },
          {
            name: t('usersInGroup', { groupNumber: '4' }),
            value: filterByGroup('4'),
            color: 'marine',
          },
          {
            name: t('usersInGroup', { groupNumber: '5' }),
            value: filterByGroup('5'),
            color: 'marine',
          },
          {
            name: t('usersInGroup', { groupNumber: '6' }),
            value: filterByGroup('6'),
            color: 'marine',
          },
          {
            name: t('esgMemberUsers'),
            value: users.filter((x) => x.role === 'STF').length,
            color: 'marine',
          },
          {
            name: t('superUsers'),
            value: users.filter((x) => x.isSuperUser).length,
            color: 'marine',
          },
          {
            name: t('guidanceServiceUsers'),
            value: users.filter((x) => x.isGuidanceService).length,
            color: 'marine',
          },
        ],
      },
    ] as HighlightCategory[];
  };

  return (
    <>
      <RouteLeavingGuard
        when={formDirty}
        shouldBlockNavigation={() => formDirty}
        from={location}
        message={t('dirtyAdminPopupMsg')}
      />
      <Container className="rolesAndPermissions">
        <PageHeader
          title={t('rolesAndPermissions')}
          side={
            <Button
              text={activeTab === '1' ? t('addESGMember') : t('addGPMember')}
              className="btn-primary"
              onClick={() => setShowMembersModal(true)}
              htmlId="btn_title"
            />
          }
          noBack
        />
        <Row>
          <Section>
            <SpinnerArea loading={loading}>
              {data && (
                <Formik
                  initialValues={{
                    users: data.map((x) => ({
                      ...x,
                      roleType: x.roleType || roleEsg,
                      group: x.group || ('0' as string | null),
                      role: x.roleType === roleGrievance ? 'STF' : x.role,
                      permissions: ['none'].concat([...x.permissions]),
                      grievanceRole: x.grievanceRole || null,
                      isGrievanceGH: x.isGrievanceGH || false,
                    })),
                  }}
                  validationSchema={buildRolesAndPermissionsSchema(t)}
                  onSubmit={(values) => {
                    const valuesForSubmit = values.users.map((u) => {
                      const user = { ...u, permissions: [...u.permissions] };
                      user.permissions.splice(0, 1); // Remover el 'none' placeholder
                      return user;
                    });
                    setDataForSubmit(valuesForSubmit);
                    setShowConfirmModal(true);
                  }}
                  enableReinitialize
                >
                  {(formik: FormikProps<{ users: RolePermissionUser[] }>) => (
                    <form className="form" onSubmit={formik.handleSubmit}>
                      <Nav tabs className="mb-3">
                        <NavItem>
                          <NavLink
                            className={classnames({ active: activeTab === '1' })}
                            onClick={() => toggleTab('1')}
                            style={{ cursor: 'pointer' }}
                          >
                            {t('esgOperations')}
                          </NavLink>
                        </NavItem>
                        <NavItem>
                          <NavLink
                            className={classnames({ active: activeTab === '2' })}
                            onClick={() => toggleTab('2')}
                            style={{ cursor: 'pointer' }}
                          >
                            {t('esGrievanceProtocol')}
                          </NavLink>
                        </NavItem>
                      </Nav>

                      <TabContent activeTab={activeTab}>
                        <TabPane tabId="1">
                          {formik.values.users.length > 0 && (
                            <Highlights
                              highlightGroups={getHighlights(formik.values.users)}
                              spread
                            />
                          )}

                          <div className="table-wrapper sticky-section w-100">
                            <Table className="rolePermission-table table--bordered table-hover table-header-sticky sortable">
                              <thead>
                                <tr>
                                  <th
                                    onClick={() => changeSort(formik, 'fullName')}
                                    className={`sortable${
                                      filter.sortField === 'fullName' ? ' current' : ''
                                    }`}
                                  >
                                    <span>
                                      {t('userName')}{' '}
                                      {filter.sortField === 'fullName' && (
                                        <FontAwesomeIcon
                                          icon={filter.sortDescending ? ARROW_DOWN : ARROW_UP}
                                        />
                                      )}
                                    </span>
                                  </th>
                                  <th
                                    onClick={() => changeSort(formik, 'organizationalUnit')}
                                    className={`sortable${
                                      filter.sortField === 'organizationalUnit' ? ' current' : ''
                                    }`}
                                  >
                                    <span>
                                      {t('organizationalUnit')}{' '}
                                      {filter.sortField === 'organizationalUnit' && (
                                        <FontAwesomeIcon
                                          icon={filter.sortDescending ? ARROW_DOWN : ARROW_UP}
                                        />
                                      )}
                                    </span>
                                  </th>
                                  <th
                                    style={{ width: '20%' }}
                                    onClick={() => changeSort(formik, 'role')}
                                    className={`sortable${
                                      filter.sortField === 'role' ? ' current' : ''
                                    }`}
                                  >
                                    <span>
                                      {t('role')}{' '}
                                      {filter.sortField === 'role' && (
                                        <FontAwesomeIcon
                                          icon={filter.sortDescending ? ARROW_DOWN : ARROW_UP}
                                        />
                                      )}
                                    </span>
                                  </th>
                                  <th
                                    style={{ width: '7%' }}
                                    onClick={() => changeSort(formik, 'group')}
                                    className={`sortable${
                                      filter.sortField === 'group' ? ' current' : ''
                                    }`}
                                  >
                                    <span>
                                      {t('group')}{' '}
                                      {filter.sortField === 'group' && (
                                        <FontAwesomeIcon
                                          icon={filter.sortDescending ? ARROW_DOWN : ARROW_UP}
                                        />
                                      )}
                                    </span>
                                  </th>
                                  <th style={{ paddingLeft: '25px' }}>{t('Env/Soc GH')}</th>
                                  <th
                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- 'isDccReviewerUser'
                                    // no existe en RolePermissionUser (columna DCC Reviewer usa `permissions`, no un
                                    // campo propio): el sort de esta columna ya estaba roto antes de esta fase, se deja
                                    // documentado tal cual en vez de inventar un comportamiento no confirmado.
                                    onClick={() => changeSort(formik, 'isDccReviewerUser' as any)}
                                    className={`sortable${
                                      filter.sortField === 'isDccReviewerUser' ? ' current' : ''
                                    }`}
                                  >
                                    <span>
                                      {t('dccReviewer')}{' '}
                                      {filter.sortField === 'isDccReviewerUser' && (
                                        <FontAwesomeIcon
                                          icon={filter.sortDescending ? ARROW_DOWN : ARROW_UP}
                                        />
                                      )}
                                    </span>
                                  </th>
                                  <th
                                    onClick={() => changeSort(formik, 'isSuperUser')}
                                    className={`sortable${
                                      filter.sortField === 'isSuperUser' ? ' current' : ''
                                    }`}
                                  >
                                    <span>
                                      {t('superUser')}{' '}
                                      {filter.sortField === 'isSuperUser' && (
                                        <FontAwesomeIcon
                                          icon={filter.sortDescending ? ARROW_DOWN : ARROW_UP}
                                        />
                                      )}
                                    </span>
                                  </th>
                                  <th
                                    onClick={() => changeSort(formik, 'isGuidanceService')}
                                    className={`sortable${
                                      filter.sortField === 'isGuidanceService' ? ' current' : ''
                                    }`}
                                  >
                                    <span>
                                      {t('guidanceServiceUser')}{' '}
                                      {filter.sortField === 'isGuidanceService' && (
                                        <FontAwesomeIcon
                                          icon={filter.sortDescending ? ARROW_DOWN : ARROW_UP}
                                        />
                                      )}
                                    </span>
                                  </th>
                                  <th
                                    onClick={() => changeSort(formik, 'modifiedOn')}
                                    className={`sortable${
                                      filter.sortField === 'modifiedOn' ? ' current' : ''
                                    }`}
                                  >
                                    <span>
                                      {t('modifiedDate')}{' '}
                                      {filter.sortField === 'modifiedOn' && (
                                        <FontAwesomeIcon
                                          icon={filter.sortDescending ? ARROW_DOWN : ARROW_UP}
                                        />
                                      )}
                                    </span>
                                  </th>
                                  <th>{t('remove')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {formik.values.users
                                  .map((user, originalIndex) => ({ user, originalIndex }))
                                  .filter(({ user }) => user.roleType === roleEsg)
                                  .map(({ user: x, originalIndex }) => (
                                    <tr key={`tr_${x.email}`}>
                                      <td>{x.fullName}</td>
                                      <td>{x.organizationalUnit}</td>
                                      <td>
                                        <FormDropdown
                                          name={`users[${originalIndex}].role`}
                                          elements={roleElements}
                                          value={x.role || ''}
                                          className="no-modal"
                                          noMargin
                                          onChangeAction={(val) => {
                                            setFormDirty(true);
                                            if (val === Role.Staff && x.group !== '0') {
                                              setTimeout(
                                                () =>
                                                  formik.setFieldValue(
                                                    `users[${originalIndex}].group`,
                                                    '0',
                                                    true
                                                  ),
                                                100
                                              );
                                            }
                                          }}
                                        />
                                      </td>
                                      <td>
                                        <FormDropdown
                                          name={`users[${originalIndex}].group`}
                                          elements={groupElements}
                                          value={x.group || ''}
                                          className="no-modal"
                                          noMargin
                                          onChangeAction={() => setFormDirty(true)}
                                        />
                                      </td>
                                      <td>
                                        {x.role === 'GH' && (
                                          <div className="d-flex">
                                            <FormCheckboxGroup
                                              name={`users[${originalIndex}].permissions`}
                                              elements={ghTypes}
                                              onChangeAction={() => setFormDirty(true)}
                                            />
                                          </div>
                                        )}
                                      </td>
                                      <td>
                                        <FormCheckboxGroup
                                          name={`users[${originalIndex}].permissions`}
                                          elements={dccReviewer}
                                          onChangeAction={() => setFormDirty(true)}
                                        />
                                      </td>
                                      <td>
                                        <FormCheckbox
                                          id={`users[${originalIndex}].isSuperUser`}
                                          name={`users[${originalIndex}].isSuperUser`}
                                          value={x.isSuperUser}
                                          text=""
                                          onChangeAction={() => setFormDirty(true)}
                                        />
                                      </td>
                                      <td>
                                        <FormCheckbox
                                          id={`users[${originalIndex}].isGuidanceService`}
                                          name={`users[${originalIndex}].isGuidanceService`}
                                          value={x.isGuidanceService}
                                          text=""
                                          onChangeAction={() => setFormDirty(true)}
                                        />
                                      </td>
                                      <td>{x.modifiedOn ? dateTimeFormat(x.modifiedOn) : 'N/A'}</td>
                                      <td>
                                        <Button
                                          className="btn-tertiary no-padding"
                                          icon="trash-alt"
                                          text=""
                                          onClick={() => removeMember(formik, x.email, x.roleType)}
                                        />
                                      </td>
                                    </tr>
                                  ))}
                              </tbody>
                            </Table>
                          </div>
                        </TabPane>

                        <TabPane tabId="2" />
                      </TabContent>

                      <TabContent activeTab={activeTab}>
                        <TabPane tabId="2">
                          <div
                            className="table-wrapper sticky-section w-100"
                            style={{ minHeight: '50vh' }}
                          >
                            <Table className="rolePermission-table table--bordered table-hover table-header-sticky sortable">
                              <thead>
                                <tr>
                                  <th
                                    onClick={() => changeSort(formik, 'fullName')}
                                    className={`sortable${
                                      filter.sortField === 'fullName' ? ' current' : ''
                                    }`}
                                  >
                                    <span>
                                      {t('userName')}{' '}
                                      {filter.sortField === 'fullName' && (
                                        <FontAwesomeIcon
                                          icon={filter.sortDescending ? ARROW_DOWN : ARROW_UP}
                                        />
                                      )}
                                    </span>
                                  </th>
                                  <th
                                    onClick={() => changeSort(formik, 'organizationalUnit')}
                                    className={`sortable${
                                      filter.sortField === 'organizationalUnit' ? ' current' : ''
                                    }`}
                                  >
                                    <span>
                                      {t('organizationalUnit')}{' '}
                                      {filter.sortField === 'organizationalUnit' && (
                                        <FontAwesomeIcon
                                          icon={filter.sortDescending ? ARROW_DOWN : ARROW_UP}
                                        />
                                      )}
                                    </span>
                                  </th>
                                  <th
                                    style={{ width: '20%' }}
                                    onClick={() => changeSort(formik, 'role')}
                                    className={`sortable${
                                      filter.sortField === 'role' ? ' current' : ''
                                    }`}
                                  >
                                    <span>
                                      {t('role')}{' '}
                                      {filter.sortField === 'role' && (
                                        <FontAwesomeIcon
                                          icon={filter.sortDescending ? ARROW_DOWN : ARROW_UP}
                                        />
                                      )}
                                    </span>
                                  </th>
                                  <th style={{ paddingLeft: '25px' }}>{t('GH')}</th>
                                  {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                                  <th style={{ width: '20%' }} />
                                  <th
                                    onClick={() => changeSort(formik, 'modifiedOn')}
                                    className={`sortable${
                                      filter.sortField === 'modifiedOn' ? ' current' : ''
                                    }`}
                                  >
                                    <span>
                                      {t('modifiedDate')}{' '}
                                      {filter.sortField === 'modifiedOn' && (
                                        <FontAwesomeIcon
                                          icon={filter.sortDescending ? ARROW_DOWN : ARROW_UP}
                                        />
                                      )}
                                    </span>
                                  </th>
                                  <th>{t('remove')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {formik.values.users
                                  .map((user, originalIndex) => ({ user, originalIndex }))
                                  .filter(({ user }) => user.roleType === roleGrievance)
                                  .map(({ user: x, originalIndex }) => (
                                    <tr key={`tr_${x.email}`}>
                                      <td>{x.fullName}</td>
                                      <td>{x.organizationalUnit}</td>
                                      <td>
                                        <FormDropdown
                                          name={`users[${originalIndex}].grievanceRole`}
                                          elements={grievanceRoleElements}
                                          value={x.grievanceRole || ''}
                                          className="no-modal"
                                          noMargin
                                          onChangeAction={(val) => {
                                            setFormDirty(true);
                                            formik.setFieldValue(
                                              `users[${originalIndex}].grievanceRole`,
                                              val,
                                              true
                                            );
                                            formik.setFieldValue(
                                              `users[${originalIndex}].isGrievanceGH`,
                                              false,
                                              false
                                            );
                                          }}
                                        />
                                      </td>
                                      <td style={{ paddingLeft: '15px' }}>
                                        {x.grievanceRole === 'GPC' && (
                                          <FormCheckbox
                                            id={`users[${originalIndex}].isGrievanceGH`}
                                            name={`users[${originalIndex}].isGrievanceGH`}
                                            value={x.isGrievanceGH}
                                            text=""
                                            onChangeAction={() => setFormDirty(true)}
                                          />
                                        )}
                                      </td>
                                      {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                                      <td style={{ width: '20%' }} />
                                      <td>{x.modifiedOn ? dateTimeFormat(x.modifiedOn) : 'N/A'}</td>
                                      <td>
                                        <Button
                                          className="btn-tertiary no-padding"
                                          icon="trash-alt"
                                          text=""
                                          onClick={() => removeMember(formik, x.email, x.roleType)}
                                        />
                                      </td>
                                    </tr>
                                  ))}
                              </tbody>
                            </Table>
                          </div>

                          {/*         <details className="mt-4">
                                        <summary className="btn btn-sm btn-outline-info">
                                          Click para ver estado de Formik
                                        </summary>
                                        <div
                                          className="bg-dark text-white p-3 mt-2"
                                          style={{ maxHeight: '400px', overflow: 'auto' }}
                                        >
                                          <pre style={{ color: '#00ff00' }}>
                                            {JSON.stringify(
                                              formik.values.users.map((u, i) => ({
                                                index: i,
                                                group: u.group,
                                                name: u.fullName,
                                                type: u.roleType,
                                                role: u.role,
                                                gRole: u.grievanceRole,
                                              })),
                                              null,
                                              2
                                            )}
                                          </pre>
                                        </div>
                                      </details> */}
                        </TabPane>
                      </TabContent>

                      <ButtonToolbar className="w-100 py-3 justify-content-end">
                        <Spinner loading={isSubmitting} />
                        <Button
                          className="btn-secondary"
                          onClick={discardChanges}
                          text={t('discardChanges')}
                          type="button"
                          disabled={isSubmitting || !formik.dirty}
                        />
                        <Button
                          className="btn-primary"
                          text={t('confirmChanges')}
                          type="submit"
                          disabled={isSubmitting || !formik.dirty}
                        />
                      </ButtonToolbar>

                      <AddMemberPopup
                        onSelect={(member) => member && addMember(formik, member)}
                        visible={showMembersModal}
                        setVisible={setShowMembersModal}
                        activeTab={activeTab}
                      />
                    </form>
                  )}
                </Formik>
              )}
            </SpinnerArea>
          </Section>
        </Row>

        {/* Modales de soporte */}
        <Modal
          visible={showErrorModal}
          setVisible={setShowErrorModal}
          title={t('adminMembersAssignedToOperationMessage')}
          customClass="extra_wide-modal"
          actions={[{ text: 'Close', action: () => setShowErrorModal(false) }]}
        >
          <div className="table-responsive px-3">
            <Table>
              <thead>
                <tr>
                  <th>{t('userName')}</th>
                  <th>{t('operation')}</th>
                </tr>
              </thead>
              <tbody>{renderErrorTable()}</tbody>
            </Table>
          </div>
        </Modal>

        <Modal
          visible={showErrorFlowsModal}
          setVisible={setShowErrorFlowsModal}
          title={t('adminMembersAssignedToFlowsMessage')}
          customClass="extra_wide-modal"
          actions={[{ text: 'Close', action: () => setShowErrorFlowsModal(false) }]}
        >
          <div className="table-responsive px-3">
            <Table>
              <thead>
                <tr>
                  <th>{t('userName')}</th>
                  <th>{t('operation')}</th>
                </tr>
              </thead>
              <tbody>{renderErrorTable()}</tbody>
            </Table>
          </div>
        </Modal>

        <ConfirmModal
          visible={showConfirmModal}
          buttonText={t('confirmChanges')}
          setVisible={setShowConfirmModal}
          action={saveUserConfiguration}
        >
          {t('confirmChangesMessage')}
        </ConfirmModal>

        <ConfirmModal
          visible={showDiscardModal}
          buttonText={t('discardChanges')}
          setVisible={setShowDiscardModal}
          action={() => reload()}
        >
          {t('confirmDiscardChangesMessage')}
        </ConfirmModal>
      </Container>
    </>
  );
};

export default RolesAndPermissions;
